import { SharedTable, SummaryBar, profitTone } from '@components';
import { useInventory } from '@hooks';
import { L } from '@lib';
import { Inventory } from '@services';
import { Utils } from '@utils';
import React from 'react';
import { InventoryFilters } from './components/InventoryFilters';
import { Columns, ExportColumns } from './InventoryTable.columns';

interface IProps {
  inventoryHook: ReturnType<typeof useInventory>;
  categoryOptions: { label: string; value: string }[];
  onEdit: (record: Inventory) => void;
  onDuplicate: (record: Inventory) => void;
  onReturnToSupplier: (record: Inventory) => void;
  onAddToPurchaseOrder: (record: Inventory) => void;
}

const InventoryTable: React.FC<IProps> = ({
  inventoryHook,
  categoryOptions,
  onEdit,
  onDuplicate,
  onReturnToSupplier,
  onAddToPurchaseOrder,
}) => {
  const {
    dateField,
    datePreset,
    customFrom,
    customTo,
    handleNameSearch,
    handleReferenceSearch,
    handlePartNumberSearch,
    handleNominalCodeSearch,
    handleInvoiceNumberSearch,
    handleCategoryFilter,
    handleSupplierFilter,
    handleDatePresetChange,
    handleCustomDateRange,
    handleDateFieldChange,
    handleHasDocumentFilter,
    handleLowStockFilter,
    handleIsInShopFilter,
  } = inventoryHook;

  return (
    <div style={{ width: '100%' }}>
      <InventoryFilters
        dateField={dateField}
        datePreset={datePreset}
        customFrom={customFrom}
        customTo={customTo}
        categoryOptions={categoryOptions}
        onDateFieldChange={handleDateFieldChange}
        onDatePresetChange={handleDatePresetChange}
        onCustomDateRange={handleCustomDateRange}
        onNameSearch={handleNameSearch}
        onReferenceSearch={handleReferenceSearch}
        onPartNumberSearch={handlePartNumberSearch}
        onNominalCodeSearch={handleNominalCodeSearch}
        onInvoiceNumberSearch={handleInvoiceNumberSearch}
        onCategoryFilter={handleCategoryFilter}
        onSupplierFilter={handleSupplierFilter}
        onHasDocumentFilter={handleHasDocumentFilter}
        onLowStockFilter={handleLowStockFilter}
        onIsInShopFilter={handleIsInShopFilter}
      />

      <SharedTable
        {...inventoryHook}
        exportable
        exportFileName="inventory"
        exportColumns={ExportColumns}
        onExportAll={inventoryHook.fetchAllForExport}
        columns={Columns({ onDuplicate, onReturnToSupplier, onAddToPurchaseOrder })}
        enableRowClick
        onRowClick={onEdit}
        mobileFilters={null}
        scrollX={undefined}
        externalPagination
        bottomMargin={70}
      />

      {/* Over every item the filters match, not the page. */}
      <SummaryBar
          pagination={{
            current: inventoryHook.currentPage,
            pageSize: inventoryHook.maxResultCount,
            total: inventoryHook.totalCount,
            onChange: (current, pageSize) => inventoryHook.handlePageChange({ current, pageSize }),
          }}
          title={inventoryHook.totals ? `${inventoryHook.totals.itemCount} ${L('filtered items')}` : undefined}
          info={L('Totals cover every item matching the filters, not just this page.')}
          loading={inventoryHook.totalsLoading}
          stats={inventoryHook.totals ? [
            { label: L('Total qty'), value: String(inventoryHook.totals.totalQuantity) },
            { label: L('Stock value (cost)'), value: Utils.formatCurrency(inventoryHook.totals.stockValueCost) },
            { label: L('Potential sale value'), value: Utils.formatCurrency(inventoryHook.totals.potentialSaleValue) },
            {
              label: L('Potential profit'),
              primary: true,
              value: Utils.formatCurrency(inventoryHook.totals.potentialProfit),
              tone: profitTone(inventoryHook.totals.potentialProfit),
            },
            {
              label: L('Average margin'),
              value: `${inventoryHook.totals.marginPercent.toFixed(1)}%`,
              tone: profitTone(inventoryHook.totals.marginPercent),
            },
          ] : []}
        />
    </div>
  );
};

export default InventoryTable;
